import { useQuery } from '@apollo/client';
import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  ActiveLozenge,
  AppContent,
  AppPage,
  Breadcrumbs,
  ErrorComponents,
  FormattedAddress,
  InactiveLozenge,
  PageHeaderV2,
  PageTabs,
  PaymentErrorLozenge,
  PricingEligibility,
  PricingPlanLozenge,
  SummaryGridItem,
  SummarySection,
  TaxExempt,
  TaxId,
  isAccess,
  isResourceAccess,
  useFeatureToggle,
  useTitle,
  useRecentlyViewed,
} from '@css-xp/base';
// @ts-ignore
import AtlassianPartnerIcon from '@css-xp/base/assets/img/icon-atlassian-partner.svg';
// @ts-ignore
import TransactionAccountIcon from '@css-xp/base/assets/img/icon-credits.svg';
import { TransactionAccountContext } from '@css-xp/base/common-context';
import { GetUpdatePaymentMethodLinkPane } from '@css-xp/base/common-sidebars';
import { CloudSiteLinkedSubscriptionTab, SubscriptionTabTransactionAccount } from '@css-xp/base/common-tabs';
import {
  FetchTransactionAccountQuery,
  PaymentMethodType,
  TransactionAccount,
  generatedTypesNew,
} from '@css-xp/graphtypes';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { UFOEventSource } from '@atlassian/temp-observability';
import { QuoteTab } from '@css-xp/quotes';
import { Permissions, Resources } from '@atlassian/cssxp-typescommon';
import {
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_QUOTE,
  ENABLE_RECENTLY_VISITED_HISTORY,
  removeContentScrollingHack,
} from '@atlassian/cssxp-utils-generic';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import AuditTrailTxA from '../../tabs/audit-trail-txa-tab/src/audit-trail-for-transaction-account';
import InvoiceTabTxA from '../../tabs/invoice-group-tabs/src/invoice-tab-transaction-account';
import OrderHistoryTxA from '../../tabs/order-history-txa-tab/src/order-history-for-transaction-account';
import TransactionAccountDetailsTab from './component/transaction-account-details-tab';
import Summary from './component/transaction-account-summary';
import { fetchTransactionAccount } from './queries/fetch-transaction-account-page';
import { AddBillingAdminsSidebar } from './sidebar-panels/add-billing-admin-sidebar';
import { AddPaymentOnTermsMethod } from './sidebar-panels/add-payment-on-terms-method-sidebar';
import { CreateInvoiceGroupSidebar } from './sidebar-panels/create-invoice-group-sidebar';
import { CreateTransactionAccountSidebar } from './sidebar-panels/create-transaction-account-sidebar';
import DeactivateBillingAccountSidebar from './sidebar-panels/deactivate-billing-account-sidebar';
import { EditBillingDetails, EditBillingDetailsSidebar } from './sidebar-panels/edit-billing-details-sidebar';
import { EditShipToPartySidebar } from './sidebar-panels/edit-ship-to-party';
import { GenerateNet30PaymentTerms } from './sidebar-panels/generate-net30-payment-term-sidebar';
import RemovePaymentMethodSidebar from './sidebar-panels/remove-payment-method-sidebar';
import { SetPaymentMethod } from './sidebar-panels/set-card-payment-default-sidebar';
import SetPricingEligibilitySidebar from './sidebar-panels/set-pricing-eligibility';
import { TaxExemptionAuditLogs } from './sidebar-panels/tax-exemption-audit-logs';
import { ViewLinkedSubscriptionsSidebar } from './sidebar-panels/view-linked-subscriptions-sidebar';
import { TransactionAccountActions } from './transaction-account-actions';
import './transaction-account-page.scss';
import { AnalyticsEventSource, ScreenEventAnalytics } from '@atlassian/temp-analytics';

const getTransactionAccount = (queryData: FetchTransactionAccountQuery) => {
  return queryData?.transactionAccount as TransactionAccount;
};

export enum SIDEBAR {
  EDIT_TRANSACTION_ACCOUNT = 'editTransactionAccount',
  CREATE_TRANSACTION_ACCOUNT = 'createTransactionAccount',
  EDIT_SHIP_TO_PARTY = 'editShipToParty',
  SET_PRICING_ELIGIBILITY = 'editBillToParty',
  CREATE_INVOICE_GROUP = 'createInvoiceGroup',
  SET_CARD_PAYMENT_DEFAULT = 'setCardPaymentDefault',
  ADD_PAYMENT_ON_TERMS_METHOD = 'addPaymentOnTermsMethod',
  ADD_BILLING_ADMINS = 'addBillingAdmins',
  TAX_EXEMPTION_AUDIT_LOGS = 'taxExemptionAuditLogs',
  VIEW_LINKED_SUBSCRIPTIONS = 'viewLinkedSubscriptions',
  DEACTIVATE_TRANSACTION_ACCOUNT = 'deactivateTransactionAccount',
  GET_UPDATE_PAYMENT_METHOD_LINK = 'get-update-payment-method-link',
  REMOVE_PAYMENT_METHOD = 'removePaymentMethod',
  GENERTE_NET30_PAYMENT_TERMS = 'generateNet30PaymentTerms',
}

const isTaxExemptFlagEnabled = true;

export const TransactionAccountPage = ({ appPageClass, title }: { appPageClass?: string; title?: string }) => {
  const [isAddressEditable, setIsAddressEditable] = React.useState<boolean>(true);
  GlobalPageLoadExperience.startPageLoad(UFOEventSource.TRANSACTION_PAGE);
  useEffect(() => {
    removeContentScrollingHack();
    GlobalPageLoadExperience.success({ force: true });
  }, []);
  const { id } = useParams<{ id: string }>();
  useTitle(id, title);

  const [isEnabled] = useFeatureToggle();
  const allowedSidebars = {
    [SIDEBAR.EDIT_TRANSACTION_ACCOUNT]: EditBillingDetailsSidebar,
    [SIDEBAR.CREATE_TRANSACTION_ACCOUNT]: CreateTransactionAccountSidebar,
    [SIDEBAR.SET_PRICING_ELIGIBILITY]: SetPricingEligibilitySidebar,
    [SIDEBAR.CREATE_INVOICE_GROUP]: CreateInvoiceGroupSidebar,
    [SIDEBAR.EDIT_SHIP_TO_PARTY]: EditShipToPartySidebar,
    [SIDEBAR.SET_CARD_PAYMENT_DEFAULT]: SetPaymentMethod,
    [SIDEBAR.ADD_PAYMENT_ON_TERMS_METHOD]: AddPaymentOnTermsMethod,
    [SIDEBAR.ADD_BILLING_ADMINS]: AddBillingAdminsSidebar,
    [SIDEBAR.TAX_EXEMPTION_AUDIT_LOGS]: TaxExemptionAuditLogs,
    [SIDEBAR.VIEW_LINKED_SUBSCRIPTIONS]: ViewLinkedSubscriptionsSidebar,
    [SIDEBAR.DEACTIVATE_TRANSACTION_ACCOUNT]: DeactivateBillingAccountSidebar,
    [SIDEBAR.GET_UPDATE_PAYMENT_METHOD_LINK]: GetUpdatePaymentMethodLinkPane,
    [SIDEBAR.REMOVE_PAYMENT_METHOD]: RemovePaymentMethodSidebar,
    [SIDEBAR.GENERTE_NET30_PAYMENT_TERMS]: GenerateNet30PaymentTerms,
  };
  const transactionAccountPageResult = useQuery<
    generatedTypesNew.FetchTransactionAccountV2Query,
    generatedTypesNew.FetchTransactionAccountV2QueryVariables
  >(fetchTransactionAccount, {
    variables: { id: id ?? '' },
    fetchPolicy: 'no-cache',
    skip: !id,
  });

  const { data, loading, error, refetch } = transactionAccountPageResult;
  const { updateRecentlyViewed } = useRecentlyViewed();
  React.useEffect(() => {
    if (data && data.transactionAccount && isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
      updateRecentlyViewed({
        entityName: title?.toLowerCase().trim().replace(/\s+/g, '_'),
        metadata: {
          transactionAccountNumber: data.transactionAccount.transactionAccountNumber,
          displayName: data.transactionAccount.displayName,
        },
      });
    }
  }, [data, title]);

  if (loading) return <LoadingIndicator />;
  if (error && !data) return <ErrorComponents.GenericErrorPage error={error} />;
  const transactionAccount = data?.transactionAccount;
  const transactionAccountId = transactionAccount?.id ?? id;
  const displayName = transactionAccount?.displayName;
  const billToAddress = transactionAccount?.billToParty;
  const billingAdmins = transactionAccount?.billingAdmins?.nodes ?? [];
  const firstInvoiceGroup = transactionAccount?.availableInvoiceGroups?.nodes?.[0];
  const availablePaymentMethods = transactionAccount?.availablePaymentMethods?.nodes ?? [];
  const shipToAddress = firstInvoiceGroup?.shipToParty;
  const isPartner = transactionAccount?.isPartner || false;
  const billToPartyPricingEligibilty = transactionAccount?.billToPartyPricingEligibilty;
  const taxExemptFlag = billToAddress?.taxExempt ? 'Yes' : 'No';
  const isActive = transactionAccount?.status?.trim().toUpperCase() === 'ACTIVE' && !isEmpty(billingAdmins);
  const partnerAccountId = transactionAccount?.partnerAccountId;
  const currentTermAccountStatus = transactionAccount?.billingAccountsList?.status;

  let isPaymentOnTermAvailable = false;
  //@ts-ignore
  const isPaymentIssue = availablePaymentMethods.some((paymentMethod) => paymentMethod?.paymentIssue === true);
  // Hide Add Payment on Terms Button on More menu if payment method already exist
  availablePaymentMethods.forEach((method: any) => {
    if (method.type === PaymentMethodType.DEFERRED) {
      isPaymentOnTermAvailable = true;
    }
  });

  // Include all props for the form except onClose
  const editBillingDetailsFormProps: EditBillingDetails = {
    billingAdmins,
    billToAddress,
    shipToAddress,
    transactionAccountId,
    billToPartyPricingEligibilty,
    invoiceGroupId: firstInvoiceGroup?.id ?? undefined,
    // @ts-ignore
    availablePaymentMethods,
    onComplete: refetch,
    isEditable: isAddressEditable,
  };

  const getHeaderLozenges = () => (
    <>
      {!isActive ? <InactiveLozenge /> : <ActiveLozenge />}&nbsp;
      {<PricingPlanLozenge plan={capitalize(billToPartyPricingEligibilty ?? 'Commercial')} />}&nbsp;
      {isPaymentIssue && <PaymentErrorLozenge />}
    </>
  );

  const renderBreadcrumbs = () => {
    if (billToAddress?.name || transactionAccount) {
      const breadcrumbTrail = [
        {
          title: billToAddress?.name ? billToAddress.name : 'Billing account',
          href: window.location.pathname,
        },
        {
          title: (transactionAccount && transactionAccount.transactionAccountNumber) ?? '',
          href: window.location.pathname,
        },
      ];

      return <Breadcrumbs items={breadcrumbTrail} />;
    }
  };

  const renderHeaderItems = () => {
    return (
      <div style={{ position: 'relative' }}>
        {/* Ternary operator to ignore '' */}
        {displayName ? displayName : 'Billing account'}&nbsp;
        <span className="subheader-lozenges">{getHeaderLozenges()}</span>
      </div>
    );
  };

  const renderSubHeaderItems = () => (
    <div className="txa-page-subheader">
      {transactionAccount?.isPartner && (
        <span data-testid="partner-logo">
          {/*// @ts-ignore */}
          <img src={AtlassianPartnerIcon} height={'15px'} alt="Atlassian partner icon" />
        </span>
      )}
    </div>
  );

  const renderSummary = () => {
    return transactionAccount ? (
      <Summary
        transactionAccountId={transactionAccountId}
        billToAddress={billToAddress}
        billToPartyPricingEligibilty={billToPartyPricingEligibilty}
        taxExemptFlag={taxExemptFlag}
        transactionAccountNumber={transactionAccount.transactionAccountNumber}
        editBillingDetailsFormProps={editBillingDetailsFormProps}
        partnerAccountId={partnerAccountId}
        setIsAddressEditable={setIsAddressEditable}
      />
    ) : (
      <SummarySection columns="1fr 1fr" className="transaction-account-summary transaction-summary-section">
        <SummaryGridItem>
          <div>
            {/* className="bill-to-address" */}
            <label>{billToAddress && billToAddress.addressLine1 ? 'Billing address' : 'No address information'}</label>
            <div>
              {/* className="address-details" */}
              <FormattedAddress
                addressLine1={billToAddress?.addressLine1}
                addressLine2={billToAddress?.addressLine2}
                city={billToAddress?.city}
                country={billToAddress?.country}
                postcode={billToAddress?.postcode}
                state={billToAddress?.state}
              />
            </div>
            <TaxId taxId={billToAddress?.taxId} />
          </div>
          <br />
          {isTaxExemptFlagEnabled && <TaxExempt taxExempt={taxExemptFlag} />}
        </SummaryGridItem>
        <SummaryGridItem>
          <PricingEligibility plan={billToPartyPricingEligibilty} />
        </SummaryGridItem>
      </SummarySection>
    );
  };

  const renderHeader = () => {
    return (
      <PageHeaderV2
        icon={
          /*// @ts-ignore */
          <img src={TransactionAccountIcon} height={'40px'} alt="TnA" />
        }
        header={renderHeaderItems()}
        subheader={renderSubHeaderItems()}
        actions={
          <TransactionAccountActions
            editBillingDetailsFormProps={editBillingDetailsFormProps}
            isPartner={isPartner}
            hasPaymentOnTerms={isPaymentOnTermAvailable}
            isActive={isActive}
            availablePaymentMethods={availablePaymentMethods}
            updatePaymentMethodLink={transactionAccount?.updatePaymentMethodLink}
            transactionAccountName={transactionAccount?.displayName}
            transactionAccountNumber={transactionAccount?.transactionAccountNumber}
            creditRequestAppLink={transactionAccount?.creditRequestAppLink}
            termPaymentStatus={currentTermAccountStatus}
          />
        }
      />
    );
  };

  // const transactionAccountNumber = transactionAccount?.transactionAccountNumber;
  return (
    <TransactionAccountContext.Provider
      value={{
        getTransactionAccount,
        ...transactionAccountPageResult,
      }}
    >
      <AppPage allowedSidebars={allowedSidebars} className={appPageClass}>
        {renderBreadcrumbs()}
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.TRANSACTION_ACCOUNT_SCREEN,
            attributes: {
              transactionAccountNumber: transactionAccount?.transactionAccountNumber,
              transactionAccountId,
              entityName: title,
              id,
            },
          }}
        />
        <AppContent>
          {renderHeader()}
          {renderSummary()}
          <PageTabs>
            <TransactionAccountDetailsTab
              id={id}
              label="Account details"
              billToAddress={billToAddress}
              onComplete={editBillingDetailsFormProps.onComplete}
              transactionAccountId={transactionAccountId}
              addBillingAdminsSidebarType={SIDEBAR.ADD_BILLING_ADMINS}
              availablePaymentMethods={availablePaymentMethods}
              transactionAccountNumber={transactionAccount?.transactionAccountNumber ?? ''}
              transactionAccountDisplayName={transactionAccount?.displayName ?? ''}
              updatePaymentMethodLink={transactionAccount?.updatePaymentMethodLink ?? ''}
              partnerAccountId={partnerAccountId}
              currentTermAccountStatus={currentTermAccountStatus}
            />
            {isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE) ? (
              <CloudSiteLinkedSubscriptionTab transactionAccountId={transactionAccountId} label="Subscriptions" />
            ) : (
              <SubscriptionTabTransactionAccount transactionAccountId={transactionAccountId} label="Subscriptions" />
            )}
            <InvoiceTabTxA
              id={id}
              transactionAccountId={transactionAccountId}
              label="Invoices"
              availablePaymentMethods={availablePaymentMethods}
            />
            {/* //TODO: Not for seq3 */}
            {/* <QuotesTabTxA label="Quotes" /> */}
            <AuditTrailTxA label="Audit trail" id={transactionAccountId} entityName="transactionAccount" />
            <OrderHistoryTxA id={id} label="Order history" />
            {isEnabled(ENABLE_QUOTE) && isResourceAccess(Resources.QUOTE_ALL) && isAccess(Permissions.QUOTE_MANAGE) && (
              <QuoteTab query={id} label="Quotes" transactionAccountId={transactionAccountId} />
            )}
          </PageTabs>
        </AppContent>
      </AppPage>
    </TransactionAccountContext.Provider>
  );
};

export default TransactionAccountPage;
